import type {
  InventoryStockLocationRecord,
  InventoryUnitRecord,
  StockTransferRecord,
} from '@/renderer/types/inventory';
import { formatReportMoney } from '@/renderer/util/reporting';
import { Button, Modal } from '@components/common';
import { useInventoryItems } from '@hooks/inventory';
import React from 'react';
import {
  TRANSFER_STATUS_COLORS,
  TRANSFER_STATUS_LABELS,
} from './stockTransferFormUtils';

interface Props {
  transfer: StockTransferRecord;
  locations: InventoryStockLocationRecord[];
  units: InventoryUnitRecord[];
  isLoadingDetail: boolean;
  onClose: () => void;
}

export const StockTransferReadOnlyView: React.FC<Props> = ({
  transfer,
  locations,
  units,
  isLoadingDetail,
  onClose,
}) => {
  const fromLocation = locations.find(
    (l) => l.id === transfer.from_stock_location_id,
  );
  const toLocation = locations.find(
    (l) => l.id === transfer.to_stock_location_id,
  );
  const lines = transfer.lines ?? [];

  const itemIds = Array.from(new Set(lines.map((l) => l.inventory_item_id)));
  const itemsQuery = useInventoryItems({
    companyId: transfer.company_id,
    page: 1,
    pageSize: Math.max(itemIds.length, 1),
    search: '',
    categoryId: 'all',
    status: 'all',
    itemIds,
  });
  const items = itemsQuery.data?.data ?? [];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`View Transfer${transfer.reference ? ` — ${transfer.reference}` : ''}`}
    >
      <div className="space-y-4">
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${TRANSFER_STATUS_COLORS[transfer.status]}`}
        >
          {TRANSFER_STATUS_LABELS[transfer.status]}
        </span>

        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-sm font-semibold text-gray-900">
            {fromLocation?.name ?? 'Unknown location'} →{' '}
            {toLocation?.name ?? 'Unknown location'}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-600 sm:grid-cols-3">
            <p>Date: {transfer.transfer_date}</p>
            <p>Reference: {transfer.reference || '—'}</p>
          </div>
          {transfer.notes ? (
            <p className="mt-2 text-sm text-gray-600">
              Notes: {transfer.notes}
            </p>
          ) : null}
        </div>

        <div>
          <p className="mb-2 text-sm font-semibold text-black">
            Items transferred
          </p>
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left">
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Item
                  </th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Quantity
                  </th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Unit
                  </th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Unit Cost
                  </th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-6 text-center text-sm text-gray-500"
                    >
                      {isLoadingDetail
                        ? 'Loading items…'
                        : 'No items recorded for this transfer.'}
                    </td>
                  </tr>
                ) : (
                  lines.map((line) => {
                    const item = items.find(
                      (i) => i.id === line.inventory_item_id,
                    );
                    const unit = item
                      ? units.find((u) => u.id === item.stock_unit_id)
                      : undefined;
                    const unitLabel = unit?.abbreviation || unit?.name || '';

                    return (
                      <tr key={line.id} className="border-t border-gray-100">
                        <td className="px-4 py-3 text-gray-900">
                          {item?.name ?? 'Unknown item'}
                          {item?.sku ? ` — ${item.sku}` : ''}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {line.quantity}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {unitLabel || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {line.unit_cost != null
                            ? formatReportMoney(line.unit_cost)
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {line.unit_cost != null
                            ? formatReportMoney(line.unit_cost * line.quantity)
                            : '—'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};
